import { useQuery } from '@tanstack/react-query';
import { profileService } from '@syscolect/domain';
import { useCurrentUser } from './useCurrentUser.js';
import { useSessionManager } from './useSessionManager.js';
import { useDispatchHookEvent } from '@rammetec/core';
/**
 * Custom hook to get the current user's selected profile data within the current organization.
 * Relies on useSessionManager for the active profile ID.
 *
 * Hook that fetches the currently selected profile data for the active session.
 */
export const useCurrentProfile = () => {
    const { currentUserId, isLoading: isUserLoading } = useCurrentUser();
    const { currentOrganizationId, currentProfileId, isLoading: isSessionLoading } = useSessionManager();
    // Query to fetch the actual profile data based on the currentProfileId
    const { data: profileData, isLoading: isProfileDataLoading, error: profileDataError } = useQuery({
        queryKey: ['profile', currentOrganizationId, currentProfileId],
        queryFn: () => {
            if (!currentOrganizationId || !currentProfileId)
                return null;
            return profileService.getProfileData(currentOrganizationId, currentProfileId);
        },
        enabled: !!currentOrganizationId && !!currentProfileId,
    });
    // Query to check if there are any valid profiles available (for UI state)
    const { data: availableProfileIds, isLoading: isAvailableProfileIdsLoading } = useQuery({
        queryKey: ['availableProfileIds', currentOrganizationId, currentUserId],
        queryFn: () => (currentOrganizationId && currentUserId)
            ? profileService.getUserProfileIds(currentOrganizationId, currentUserId)
            : Promise.resolve([]),
        enabled: !!currentOrganizationId && !!currentUserId && !isUserLoading,
    });
    const hasValidProfileAvailable = (availableProfileIds?.length ?? 0) > 0;
    const isLoading = isUserLoading || isSessionLoading || isProfileDataLoading || isAvailableProfileIdsLoading;
    useDispatchHookEvent('useCurrentProfile', {
        profileData,
        isLoading,
        error: profileDataError,
        hasValidProfileAvailable,
    });
    return {
        profileData,
        isLoading,
        error: profileDataError,
        hasValidProfileAvailable,
        availableProfilesCount: availableProfileIds?.length ?? 0,
    };
};
//# sourceMappingURL=useCurrentProfile.js.map